import { useEffect, useRef, useState } from "react";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { cn } from "@/lib/utils";

export interface PropertyHeroProps {
  /** Display address. Used for the fallback card and iframe accessibility labels. */
  address: string;
  /** Latitude in decimal degrees. When omitted (or `lon` omitted), the fallback card renders. */
  lat?: number;
  /** Longitude in decimal degrees. When omitted (or `lat` omitted), the fallback card renders. */
  lon?: number;
  /** `strip` (default) = side-by-side 280px tall. `stacked` = each panel 280px stacked vertically. */
  aspect?: "strip" | "stacked";
  /** Optional className for the outer wrapper. */
  className?: string;
}

// `VITE_GOOGLE_MAPS_EMBED_KEY` is shared across three Google Maps Platform APIs:
//   - Maps Embed API (satellite panel iframe)
//   - Street View Static API (panorama metadata lookup by address)
//   - Maps JavaScript API (interactive Street View panorama widget)
// All three must be enabled on the same HTTP-referrer-restricted browser key.
// The env var name is preserved for deploy/secret compatibility even though it
// now also covers the JS API; see the runtime guidance in .env.example.
//
// We use the JS API's `StreetViewPanorama` (not the Embed API iframe) to render
// the panorama because the Embed API iframe shows an uncontrollable address
// overlay in the top-left labeling the *panorama's* canonical address, not the
// property's. The JS API lets us pass `addressControl: false` to suppress it.
const EMBED_KEY = import.meta.env.VITE_GOOGLE_MAPS_EMBED_KEY as
  | string
  | undefined;

// `setOptions` is module-level config for `@googlemaps/js-api-loader@2.x`'s new
// functional API. Must be called before the first `importLibrary()` call.
// Idempotent for repeated calls with identical options; safe at module scope.
if (EMBED_KEY) {
  setOptions({ key: EMBED_KEY, v: "weekly" });
}

function satelliteEmbedSrc(address: string, lat: number, lon: number): string {
  if (EMBED_KEY) {
    // `place` mode (instead of `view`) renders a default pin marker at the
    // geocoded query result and auto-centers the camera on that geocoded
    // position. Do NOT pass `center=` — when both `q` and `center` are set, the
    // camera uses `center` while the pin sits on the `q` result, which puts the
    // pin off-viewport whenever the stored lat/lon diverges from the geocoded
    // address (rooftop centroid vs. street centroid, etc.).
    const q = encodeURIComponent(address);
    return `https://www.google.com/maps/embed/v1/place?key=${EMBED_KEY}&q=${q}&zoom=18&maptype=satellite`;
  }
  return `https://maps.google.com/maps?q=${lat},${lon}&t=k&z=18&output=embed`;
}

// Forward bearing in degrees clockwise from north — see e.g. Movable Type's
// Latitude/Longitude formulary. Used to aim the Street View camera from the
// panorama Google chose toward the building's actual coordinates, because the
// panorama Google picks for an address query is the *nearest panorama with
// coverage*, not necessarily one parked in front of the building. On streets
// with sparse Street View coverage (e.g. private townhouse cul-de-sacs) the
// nearest panorama can be tens of meters away and oriented down the road.
function bearingDeg(
  fromLat: number,
  fromLon: number,
  toLat: number,
  toLon: number,
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const phi1 = toRad(fromLat);
  const phi2 = toRad(toLat);
  const dLon = toRad(toLon - fromLon);
  const y = Math.sin(dLon) * Math.cos(phi2);
  const x =
    Math.cos(phi1) * Math.sin(phi2) -
    Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Haversine distance in meters. Used to decide whether the panorama is close
// enough to the building that Google's default camera orientation is already
// good (skip heading) vs. far enough that we need to aim it.
function distanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const dPhi = toRad(lat2 - lat1);
  const dLambda = toRad(lon2 - lon1);
  const a =
    Math.sin(dPhi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Panorama-to-building distance at which we start aiming the camera. Below
// this, the panorama is likely parked in front of the building and Google's
// default orientation is usually correct; aiming would rotate the camera into
// the building instead of along the line of sight.
const PANO_AIM_THRESHOLD_METERS = 15;

function openInMapsHref(address: string): string {
  return `https://maps.google.com/maps?q=${encodeURIComponent(address)}`;
}

interface PanelProps {
  src: string;
  label: string;
  address: string;
}

function Panel({ src, label, address }: PanelProps) {
  return (
    <div className="relative h-full w-full overflow-hidden rounded-md border border-[color:var(--color-border)]">
      <iframe
        src={src}
        title={`${label} — ${address}`}
        loading="lazy"
        allowFullScreen
        className="h-full w-full border-0"
      />
      <span className="absolute bottom-2 left-2 z-20 rounded-md bg-background/80 px-2 py-1 text-xs font-medium text-[color:var(--color-text-hi)] backdrop-blur-sm">
        {label}
      </span>
    </div>
  );
}

interface StreetViewPanoramaPanelProps {
  panoId: string;
  heading: number | null;
  label: string;
  address: string;
}

function StreetViewPanoramaPanel({
  panoId,
  heading,
  label,
  address,
}: StreetViewPanoramaPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [unavailable, setUnavailable] = useState(false);

  // Reset the unavailable latch whenever the panorama identity changes. Without
  // this, a transient failure on one property would stick the panel on the
  // fallback card across every subsequent property — the fallback path used to
  // skip rendering the container div, so later mount attempts had no DOM target.
  useEffect(() => {
    setUnavailable(false);
  }, [panoId, heading]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !EMBED_KEY) return;
    let cancelled = false;
    let statusListener: google.maps.MapsEventListener | null = null;
    importLibrary("streetView")
      .then((lib) => {
        if (cancelled) return;
        // `addressControl: false` is the whole point of using the JS API over
        // the Embed iframe — it removes Google's top-left address label, which
        // is misleading because it shows the panorama's canonical address
        // rather than the property we're viewing.
        const options: google.maps.StreetViewPanoramaOptions = {
          pano: panoId,
          addressControl: false,
          showRoadLabels: false,
          fullscreenControl: true,
          motionTrackingControl: false,
          panControl: false,
          zoomControl: false,
        };
        if (heading != null) {
          options.pov = { heading, pitch: 0 };
        }
        const panorama = new lib.StreetViewPanorama(container, options);
        statusListener = panorama.addListener("status_changed", () => {
          if (panorama.getStatus() !== google.maps.StreetViewStatus.OK) {
            setUnavailable(true);
          }
        });
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
      if (statusListener) statusListener.remove();
      // StreetViewPanorama has no explicit destroy; clearing the container
      // detaches the rendered DOM so the next mount starts clean.
      container.innerHTML = "";
    };
  }, [panoId, heading]);

  // Container always renders so the mount effect has a valid DOM target after
  // a prior failure. The unavailable fallback overlays the container — when a
  // subsequent panoId loads successfully, the overlay is removed and the
  // already-mounted panorama is revealed underneath.
  return (
    <div className="relative h-full w-full overflow-hidden rounded-md border border-[color:var(--color-border)]">
      <div
        ref={containerRef}
        className="h-full w-full"
        aria-label={`${label} — ${address}`}
      />
      {unavailable && (
        <div className="absolute inset-0 flex flex-col items-start justify-center gap-2 bg-[color:var(--color-surface)] p-4">
          <p className="text-sm text-[color:var(--color-text-md)]">{address}</p>
          <a
            href={openInMapsHref(address)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-[color:var(--color-accent)] hover:underline"
          >
            Open Street View in Google Maps →
          </a>
        </div>
      )}
      <span className="absolute bottom-2 left-2 z-20 rounded-md bg-background/80 px-2 py-1 text-xs font-medium text-[color:var(--color-text-hi)] backdrop-blur-sm">
        {label}
      </span>
    </div>
  );
}

function StreetViewLoadingPanel() {
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
      <span className="typo-label text-[color:var(--color-text-lo)]">
        Loading Street View…
      </span>
      <span className="absolute bottom-2 left-2 z-20 rounded-md bg-background/80 px-2 py-1 text-xs font-medium text-[color:var(--color-text-hi)] backdrop-blur-sm">
        Street View
      </span>
    </div>
  );
}

function StreetViewUnavailablePanel({ address }: { address: string }) {
  return (
    <div className="relative flex h-full w-full flex-col items-start justify-center gap-2 overflow-hidden rounded-md border border-[color:var(--color-border)] p-4">
      <p className="text-sm text-[color:var(--color-text-md)]">{address}</p>
      <a
        href={openInMapsHref(address)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm font-medium text-[color:var(--color-accent)] hover:underline"
      >
        Open Street View in Google Maps →
      </a>
      <span className="absolute bottom-2 left-2 z-20 rounded-md bg-background/80 px-2 py-1 text-xs font-medium text-[color:var(--color-text-hi)] backdrop-blur-sm">
        Street View
      </span>
    </div>
  );
}

type PanoState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "found"; panoId: string; lat: number; lon: number }
  | { status: "unavailable" };

export function PropertyHero({
  address,
  lat,
  lon,
  aspect = "strip",
  className,
}: PropertyHeroProps) {
  const hasCoords =
    lat !== undefined &&
    lon !== undefined &&
    Number.isFinite(lat) &&
    Number.isFinite(lon);

  const [pano, setPano] = useState<PanoState>({ status: "idle" });

  useEffect(() => {
    if (!EMBED_KEY || !hasCoords || !address) {
      // Codex P2: early-exit must land in `unavailable`, not `idle`. The
      // render path treats both `idle` and `loading` as the loading panel,
      // so leaving `pano` as `idle` here produced an endless "Loading Street
      // View…" state for call sites that pass `address ?? ""` (e.g.
      // opportunities/detail.tsx, customers/detail.tsx) on records whose
      // formatted_address is null.
      setPano({ status: "unavailable" });
      return;
    }
    const ctrl = new AbortController();
    setPano({ status: "loading" });
    fetch(
      `https://maps.googleapis.com/maps/api/streetview/metadata?location=${encodeURIComponent(address)}&key=${EMBED_KEY}`,
      { signal: ctrl.signal },
    )
      .then((r) => r.json())
      .then(
        (data: {
          status?: string;
          pano_id?: string;
          location?: { lat?: number; lng?: number };
        }) => {
          if (
            data.status === "OK" &&
            data.pano_id &&
            typeof data.location?.lat === "number" &&
            typeof data.location?.lng === "number"
          ) {
            setPano({
              status: "found",
              panoId: data.pano_id,
              lat: data.location.lat,
              lon: data.location.lng,
            });
          } else {
            setPano({ status: "unavailable" });
          }
        },
      )
      .catch((err: unknown) => {
        if (
          err &&
          typeof err === "object" &&
          "name" in err &&
          (err as { name: string }).name === "AbortError"
        ) {
          return;
        }
        setPano({ status: "unavailable" });
      });
    return () => ctrl.abort();
  }, [address, hasCoords]);

  if (!hasCoords) {
    return (
      <div
        className={cn(
          "flex h-[280px] flex-col items-start justify-center gap-2 rounded-md border border-[color:var(--color-border)] p-4",
          className,
        )}
      >
        <p className="text-sm text-[color:var(--color-text-md)]">
          {address || "No address"}
        </p>
        <a
          href={openInMapsHref(address)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-[color:var(--color-accent)] hover:underline"
        >
          Get Street View →
        </a>
      </div>
    );
  }

  const satelliteSrc = satelliteEmbedSrc(address, lat, lon);

  let streetViewPanel: React.ReactNode;
  if (!EMBED_KEY) {
    streetViewPanel = <StreetViewUnavailablePanel address={address} />;
  } else if (pano.status === "loading" || pano.status === "idle") {
    streetViewPanel = <StreetViewLoadingPanel />;
  } else if (pano.status === "found") {
    const offsetMeters = distanceMeters(pano.lat, pano.lon, lat, lon);
    const heading =
      offsetMeters >= PANO_AIM_THRESHOLD_METERS
        ? bearingDeg(pano.lat, pano.lon, lat, lon)
        : null;
    streetViewPanel = (
      <StreetViewPanoramaPanel
        panoId={pano.panoId}
        heading={heading}
        label="Street View"
        address={address}
      />
    );
  } else {
    streetViewPanel = <StreetViewUnavailablePanel address={address} />;
  }

  if (aspect === "stacked") {
    return (
      <div className={cn("grid grid-rows-2 gap-2 [&>*]:h-[280px]", className)}>
        <Panel src={satelliteSrc} label="Satellite" address={address} />
        {streetViewPanel}
      </div>
    );
  }

  return (
    <div className={cn("grid h-[280px] grid-cols-2 gap-2", className)}>
      <Panel src={satelliteSrc} label="Satellite" address={address} />
      {streetViewPanel}
    </div>
  );
}
